import { useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router'

import VideocamIcon from '@mui/icons-material/Videocam'

import Snackbar from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'

import usePubNub from '@shared/hooks/src/usePubNub'
import { useMe } from '@shared/providers/src/MeProvider'

import useFormatMessage from '@hooks/useFormatMessage'

import { NotificationCard, NotificationIcon, styles } from '../Notifications.utils'

/**
 * JoinAppointment component that listens for 'join_appointment' actions via PubNub
 * and displays a notification allowing the user to join the appointment.
 *
 * Carries the green of the join button it leads to, so the notification and the thing
 * it opens are recognisably the same errand.
 */
export default function JoinAppointment() {
  const me = useMe()
  const formatMessage = useFormatMessage()
  const currentPath = useLocation().pathname
  const testId = 'join-appointment-notification'

  const [open, setOpen] = useState(false)
  const [appointmentId, setAppointmentId] = useState(undefined)

  const handleClose = () => setOpen(false)

  usePubNub(`user_${me.id}`, ({ action, attributes }) => {
    if (action !== 'join_appointment') return

    setAppointmentId(attributes.id)
    setOpen(true)
  })

  return (
    <Snackbar
      open={open}
      onClose={(event, reason) => {
        if (reason === 'escapeKeyDown') handleClose()
      }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      data-testid={`${testId}-snackbar`}
    >
      <NotificationCard
        testId={testId}
        onClose={handleClose}
        leading={<NotificationIcon icon={VideocamIcon} color="success" />}
        title={
          <Typography data-testid={`${testId}-title`} id={`${testId}-title`} sx={styles.title}>
            {formatMessage('notifications.toast.joinAppointment.title')}
          </Typography>
        }
        actionProps={{
          component: RouterLink,
          to: `/app/visits/${appointmentId}`,
          replace: currentPath.startsWith('/app/visits'),
          onClick: handleClose,
        }}
      >
        <Typography variant="body2" data-testid={`${testId}-description`} component="p" id={`${testId}-description`} sx={styles.supporting}>
          {formatMessage('notifications.toast.joinAppointment.description')}
        </Typography>
      </NotificationCard>
    </Snackbar>
  )
}
